import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'

interface FamilyMember {
  id: string
  relationship: string
  member_name: string
  member_sex: string | null
  member_status: string
  member_birth_year: number | null
  marriage_year: number | null
  born_at_principal_age: number | null
  linked_character_id: string | null
  stub_data: Record<string, unknown>
}

interface Props {
  characterId: string
  gameYear?: number
}



function formatDeathOrAge(m: FamilyMember, gameYear: number): string {
  const birthYear = m.member_birth_year
  const deathYear = m.stub_data?.deathYear as number | null | undefined
  const diedAge = m.stub_data?.diedAge as number | null | undefined

  if (!birthYear) return m.member_status === 'deceased' ? 'deceased' : ''

  if (m.member_status === 'deceased') {
    if (deathYear != null && diedAge != null) {
      return `born ${birthYear}, died ${deathYear} aged ${diedAge}`
    } else if (diedAge != null) {
      return `born ${birthYear}, died ${birthYear + diedAge} aged ${diedAge}`
    } else if (deathYear != null) {
      return `born ${birthYear}, died ${deathYear} aged ${deathYear - birthYear}`
    }
    return `born ${birthYear}, deceased`
  }

  const currentAge = gameYear - birthYear
  return `born ${birthYear}, age ${currentAge}`
}

function formatChildDetail(m: FamilyMember, gameYear: number): string {
  const parts: string[] = []
  if (m.member_sex) parts.push(m.member_sex)
  parts.push(formatDeathOrAge(m, gameYear))
  if (m.born_at_principal_age != null) parts.push(`when ${m.born_at_principal_age}`)
  return parts.filter(Boolean).length > 0 ? `(${parts.filter(Boolean).join(', ')})` : ''
}

function formatSpouseDetail(m: FamilyMember, gameYear: number): string {
  const parts: string[] = []
  if (m.member_sex) parts.push(m.member_sex)
  parts.push(formatDeathOrAge(m, gameYear))

  if (m.marriage_year) {
    const deathYear = m.stub_data?.deathYear as number | null | undefined
    if (m.member_status === 'deceased' && deathYear) {
      const duration = deathYear - m.marriage_year
      parts.push(`married ${m.marriage_year}, ${duration} years`)
    } else {
      const duration = gameYear - m.marriage_year
      parts.push(`married ${m.marriage_year}, ${duration} years`)
    }
  }

  return parts.filter(Boolean).length > 0 ? `(${parts.filter(Boolean).join(', ')})` : ''
}

function formatParentDetail(m: FamilyMember, gameYear: number): string {
  return formatDeathOrAge(m, gameYear) ? `(${formatDeathOrAge(m, gameYear)})` : ''
}

function formatSiblingDetail(m: FamilyMember, gameYear: number): string {
  const parts: string[] = []
  if (m.member_sex) parts.push(m.member_sex)
  parts.push(formatDeathOrAge(m, gameYear))
  return parts.filter(Boolean).length > 0 ? `(${parts.filter(Boolean).join(', ')})` : ''
}

export function FamilyPanel({ characterId, gameYear = 720 }: Props) {
  const { data: members, isLoading } = useQuery({
    queryKey: ['family-members', characterId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('character_family_members')
        .select('*')
        .eq('character_id', characterId)
        .order('sort_order')
      if (error) throw error
      return data as FamilyMember[]
    },
  })

  if (isLoading) return <div className="text-sm text-ink-light p-4">Loading family...</div>

  const parents = members?.filter(m => m.relationship === 'parent') ?? []
  const siblings = members?.filter(m => m.relationship === 'sibling') ?? []
  const spouses = members?.filter(m => m.relationship === 'spouse') ?? []
  const children = members?.filter(m => m.relationship === 'child') ?? []

  if (!members || members.length === 0) {
    return (
      <div className="border border-parchment-dark rounded p-4 mb-4 bg-white">
        <h3 className="font-cinzel font-semibold text-sm text-accent uppercase tracking-wider mb-3 pb-2 border-b-2 border-accent/20">Family</h3>
        <p className="text-sm text-ink-light">No family data available.</p>
      </div>
    )
  }

  const renderMember = (m: FamilyMember, detail: string) => (
    <div key={m.id} className="py-0.5 text-sm">
      <span className="font-medium">{m.member_name}</span>
      {detail && <span className="text-ink-light text-xs ml-1">{detail}</span>}
    </div>
  )

  return (
    <div className="border border-parchment-dark rounded p-4 mb-4 bg-white">
      <h3 className="font-cinzel font-semibold text-sm text-accent uppercase tracking-wider mb-3 pb-2 border-b-2 border-accent/20">Family</h3>

      {parents.length > 0 && (
        <div className="mb-3">
          <div className="text-xs font-semibold text-ink-light uppercase mb-1">Parents</div>
          {parents.map(p => renderMember(p, formatParentDetail(p, gameYear)))}
        </div>
      )}

      {siblings.length > 0 && (
        <div className="mb-3">
          <div className="text-xs font-semibold text-ink-light uppercase mb-1">Siblings ({siblings.length})</div>
          {siblings.map(s => renderMember(s, formatSiblingDetail(s, gameYear)))}
        </div>
      )}

      {spouses.length > 0 && (
        <div className="mb-3">
          <div className="text-xs font-semibold text-ink-light uppercase mb-1">Spouses</div>
          {spouses.map(sp => renderMember(sp, formatSpouseDetail(sp, gameYear)))}
        </div>
      )}

      {children.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-ink-light uppercase mb-1">
            Children ({children.filter(c => c.member_status === 'alive').length} living of {children.length})
          </div>
          {children.map(c => renderMember(c, formatChildDetail(c, gameYear)))}
        </div>
      )}
    </div>
  )
}
